import {
	Error,
	FetchGamificationBehaviorsData,
	FetchGamificationBehaviorsParams,
	FetchGamificationLeaderboardByLeaderboardData,
	FetchGamificationLeaderboardByLeaderboardParams,
	FetchGamificationLeaderboardRankingMeByLeaderboardIdData,
	FetchGamificationLeaderboardRankingsData,
	FetchGamificationLeaderboardRankingsParams,
	FetchGamificationLeaderboardsData,
	FetchGamificationLeaderboardsParams,
	FetchGamificationMeLeaderboardsData,
	FetchGamificationMeLeaderboardsParams,
	FetchGamificationRewardConfigsData,
	FetchGamificationRewardConfigsParams,
	FetchGamificationUserMeLoginStreakData,
	PostGamificationFormParticipationByFormIdData
} from '../../../models/__generated/data-contracts';
import { HttpClient, RequestParams } from './http-client';

export class GamificationApi<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * No description
	 *
	 * @tags GamificationService:Reward
	 * @name FetchGamificationRewardConfigs
	 * @summary Get reward configs endpoint
	 * @request GET:/api/acl-service/v1/front/gamification-service/reward-configs
	 * @secure
	 * @response `200` `FetchGamificationRewardConfigsData` Successfully filtered reward config list ([simple paginationMode enabled](https://bookstack.chiliz.com/books/development-guides/page/pagination))
	 * @response `400` `Error`
	 */
	fetchGamificationRewardConfigs = (query: FetchGamificationRewardConfigsParams, params: RequestParams = {}) =>
		this.http.request<FetchGamificationRewardConfigsData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/reward-configs`,
			method: 'GET',
			query: query,
			secure: true,
			...params
		});
	/**
	 * No description
	 *
	 * @tags GamificationService:Leaderboard
	 * @name FetchGamificationLeaderboards
	 * @summary Search leaderboards endpoint
	 * @request GET:/api/acl-service/v1/front/gamification-service/leaderboards
	 * @secure
	 * @response `200` `FetchGamificationLeaderboardsData` Successfully filtered leaderboard list ([simple paginationMode enabled](https://bookstack.chiliz.com/books/development-guides/page/pagination))
	 * @response `400` `Error`
	 */
	fetchGamificationLeaderboards = (query: FetchGamificationLeaderboardsParams, params: RequestParams = {}) =>
		this.http.request<FetchGamificationLeaderboardsData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/leaderboards`,
			method: 'GET',
			query: query,
			secure: true,
			...params
		});
	/**
	 * No description
	 *
	 * @tags GamificationService:Leaderboard
	 * @name FetchGamificationMeLeaderboards
	 * @summary Search leaderboards endpoint with user
	 * @request GET:/api/acl-service/v1/front/gamification-service/me/leaderboards
	 * @secure
	 * @response `200` `FetchGamificationMeLeaderboardsData` Successfully filtered leaderboard list ([simple paginationMode enabled](https://bookstack.chiliz.com/books/development-guides/page/pagination))
	 * @response `400` `Error`
	 */
	fetchGamificationMeLeaderboards = (query: FetchGamificationMeLeaderboardsParams, params: RequestParams = {}) =>
		this.http.request<FetchGamificationMeLeaderboardsData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/me/leaderboards`,
			method: 'GET',
			query: query,
			secure: true,
			...params
		});
	/**
	 * No description
	 *
	 * @tags GamificationService:Leaderboard
	 * @name FetchGamificationLeaderboardByLeaderboard
	 * @summary Get a leaderboard endpoint
	 * @request GET:/api/acl-service/v1/front/gamification-service/leaderboard/{leaderboard}
	 * @secure
	 * @response `200` `FetchGamificationLeaderboardByLeaderboardData` Successfully got a leaderboard
	 * @response `400` `Error`
	 * @response `404` `Error`
	 */
	fetchGamificationLeaderboardByLeaderboard = (
		{ leaderboard, ...query }: FetchGamificationLeaderboardByLeaderboardParams,
		params: RequestParams = {}
	) =>
		this.http.request<FetchGamificationLeaderboardByLeaderboardData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/leaderboard/${leaderboard}`,
			method: 'GET',
			query: query,
			secure: true,
			...params
		});
	/**
	 * @description Orderby not available, will always be sorted by leaderboard_id, rank ASC
	 *
	 * @tags GamificationService:LeaderboardRanking
	 * @name FetchGamificationLeaderboardRankings
	 * @summary Search leaderboard rankings
	 * @request GET:/api/acl-service/v1/front/gamification-service/leaderboard-rankings
	 * @secure
	 * @response `200` `FetchGamificationLeaderboardRankingsData` Successfully filtered leaderboard ranking list, note that if leaderboardIds or chilizUserIds is missing, it will return an empty array. ([simple paginationMode enabled](https://bookstack.chiliz.com/books/development-guides/page/pagination))
	 * @response `400` `Error`
	 */
	fetchGamificationLeaderboardRankings = (
		query: FetchGamificationLeaderboardRankingsParams,
		params: RequestParams = {}
	) =>
		this.http.request<FetchGamificationLeaderboardRankingsData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/leaderboard-rankings`,
			method: 'GET',
			query: query,
			secure: true,
			...params
		});
	/**
	 * No description
	 *
	 * @tags GamificationService:LeaderboardRanking
	 * @name FetchGamificationLeaderboardRankingMeByLeaderboardId
	 * @summary Get logged in user's ranking on specific leaderboard
	 * @request GET:/api/acl-service/v1/front/gamification-service/leaderboard-ranking/{leaderboardId}/me
	 * @secure
	 * @response `200` `FetchGamificationLeaderboardRankingMeByLeaderboardIdData` Successfully got logged in user's ranking on specific leaderboard
	 * @response `400` `Error`
	 * @response `404` `Error`
	 */
	fetchGamificationLeaderboardRankingMeByLeaderboardId = (leaderboardId: string, params: RequestParams = {}) =>
		this.http.request<FetchGamificationLeaderboardRankingMeByLeaderboardIdData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/leaderboard-ranking/${leaderboardId}/me`,
			method: 'GET',
			secure: true,
			...params
		});
	/**
	 * No description
	 *
	 * @tags GamificationService:UserEvents
	 * @name FetchGamificationUserMeLoginStreak
	 * @summary Automatically Generated
	 * @request GET:/api/acl-service/v1/front/gamification-service/user/me/login-streak
	 * @response `200` `FetchGamificationUserMeLoginStreakData` Successfully get user login streak
	 * @response `400` `Error`
	 */
	fetchGamificationUserMeLoginStreak = (params: RequestParams = {}) =>
		this.http.request<FetchGamificationUserMeLoginStreakData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/user/me/login-streak`,
			method: 'GET',
			...params
		});
	/**
	 * No description
	 *
	 * @tags GamificationService:Form
	 * @name PostGamificationFormParticipationByFormId
	 * @summary Create a new FormParticipation
	 * @request POST:/api/acl-service/v1/front/gamification-service/form-participation/{formId}
	 * @secure
	 * @response `201` `PostGamificationFormParticipationByFormIdData` FormParticipation has been successfully created
	 * @response `400` `Error`
	 * @response `404` `Error`
	 */
	postGamificationFormParticipationByFormId = (formId: string, params: RequestParams = {}) =>
		this.http.request<PostGamificationFormParticipationByFormIdData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/form-participation/${formId}`,
			method: 'POST',
			secure: true,
			...params
		});
	/**
	 * No description
	 *
	 * @tags GamificationService:Behavior
	 * @name FetchGamificationBehaviors
	 * @summary Search behaviors endpoint
	 * @request GET:/api/acl-service/v1/front/gamification-service/behaviors
	 * @response `200` `FetchGamificationBehaviorsData` Successfully filtered behavior list ([simple paginationMode enabled](https://bookstack.chiliz.com/books/development-guides/page/pagination))
	 * @response `400` `Error`
	 */
	fetchGamificationBehaviors = (query: FetchGamificationBehaviorsParams, params: RequestParams = {}) =>
		this.http.request<FetchGamificationBehaviorsData, Error>({
			path: `/api/acl-service/v1/front/gamification-service/behaviors`,
			method: 'GET',
			query: query,
			...params
		});
}
